import React from "react";
import { Box, Option } from "@mui/joy";
import { SCreate } from "../../styles/StyledMUI";
import { textTokens } from "../../styles/globalStyles";
import DarkSelect from "../common/DarkSelect";
import DarkTypography from "../common/DarkTypography";
import IconButtonCTA from "../common/IconButtonCTA";
import Pagination from "../common/Pagination";
import { Permission, usePermissions } from "../../contexts/app/permissions";
import { useConfirmationModal } from "../../contexts/app/confirmation";
import { useCustomer } from "../../contexts/api";
import useRentals from "./hooks/useRentals";
import useRentalCatalogue from "./hooks/useRentalCatalogue";
import RentalCreateModal from "./display/RentalCreateModal";
import RentalEditModal from "./display/RentalEditModal";
import RentalCard from "./display/RentalCard";
import { DEFAULT_PAGINATION_LIMIT } from "../../utils/paginationHelpers";
import type { Rental, RentalLifeStatus } from "../../api/rental/Rental.typed";
import type { ZekayCustomer } from "../../api/customers/Customer.typed";

const STATUS_FILTER_ALL = "__all";

export default function RentalPage() {
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(Permission.RENTAL_EDIT);
  const { showConfirmation } = useConfirmationModal();
  const { getCustomersBatch } = useCustomer();

  const { rentals, loading, refresh, create, update, remove, returnItem, removeItem, reload } =
    useRentals();
  const { products } = useRentalCatalogue();

  const [statusFilter, setStatusFilter] = React.useState<string>(STATUS_FILTER_ALL);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editing, setEditing] = React.useState<Rental | null>(null);
  const [customers, setCustomers] = React.useState<Map<number, ZekayCustomer>>(new Map());
  const [currentPage, setCurrentPage] = React.useState(1);
  const [itemsPerPage, setItemsPerPage] = React.useState(DEFAULT_PAGINATION_LIMIT);

  React.useEffect(() => {
    const ids = Array.from(new Set(rentals.map((r) => r.customer_id))).filter(
      (id) => !customers.has(id),
    );
    if (ids.length === 0) return;

    let cancelled = false;
    (async () => {
      const list = await getCustomersBatch(ids);
      if (cancelled || !list) return;

      setCustomers((prev) => {
        const next = new Map(prev);
        for (const c of list) {
          if (c.Id != null) next.set(c.Id, c);
        }
        return next;
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [rentals, customers, getCustomersBatch]);

  const filtered = React.useMemo(() => {
    if (statusFilter === STATUS_FILTER_ALL) return rentals;
    return rentals.filter((r) => r.status === (statusFilter as RentalLifeStatus));
  }, [rentals, statusFilter]);

  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));

  React.useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const pageRentals = React.useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filtered.slice(start, start + itemsPerPage);
  }, [filtered, currentPage, itemsPerPage]);

  const customerName = (id: number) => {
    const c = customers.get(id);
    if (!c) return `#${id}`;
    const full = [c.FirstName, c.LastName].filter(Boolean).join(" ").trim();
    return full || `#${id}`;
  };

  const handleCreate = () => setCreateOpen(true);

  const handleSubmit = async (req: Parameters<typeof create>[0]) => {
    const created = await create(req);
    if (created) setEditing(created);
  };

  const handleOpenEdit = async (r: Rental) => {
    const fresh = (await reload(r.id)) ?? r;
    setEditing(fresh);
  };

  const handleDelete = async (r: Rental) => {
    const ok = await showConfirmation("Supprimer la location", `Supprimer la location #${r.id} ?`);
    if (ok) await remove(r.id);
  };

  const handleStatusChange = async (r: Rental, next: Rental["status"]) => {
    const labels: Record<Rental["status"], string> = {
      draft: "remettre en brouillon",
      active: "démarrer",
      returned: "clore (passer en retour)",
      cancelled: "annuler",
    };
    const ok = await showConfirmation(
      "Changer le statut",
      `Confirmer : ${labels[next]} la location #${r.id} ?`,
    );
    if (!ok) return;
    await update(r.id, { status: next });
    await refresh();
  };

  const refreshEditing = async (id: number) => {
    const fresh = await reload(id);
    if (fresh) setEditing(fresh);
    await refresh();
  };

  const showEmpty = !loading && pageRentals.length === 0;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          p: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: 1.5,
            mb: 2,
            flexWrap: "wrap",
          }}
        >
          <DarkSelect
            size="sm"
            value={statusFilter}
            onChange={(_, v) => v != null && setStatusFilter(v as string)}
            placeholder="Statut"
            aria-label="Filtrer par statut"
            sx={{
              minWidth: { xs: "100%", sm: 200 },
              maxWidth: { sm: 360 },
              flex: { sm: "0 1 auto" },
            }}
          >
            <Option value={STATUS_FILTER_ALL}>Tous statuts</Option>
            <Option value="draft">Brouillon</Option>
            <Option value="active">En cours</Option>
            <Option value="returned">Retournée</Option>
            <Option value="cancelled">Annulée</Option>
          </DarkSelect>

          {canEdit && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
              <IconButtonCTA
                aria-label="Nouvelle location"
                ctaVariant="accent"
                variant="plain"
                onClick={handleCreate}
              >
                <SCreate />
              </IconButtonCTA>
            </Box>
          )}
        </Box>

        {showEmpty && (
          <DarkTypography level="body-md" sx={{ color: textTokens.color.dimmed, mt: 2 }}>
            Aucune location.
          </DarkTypography>
        )}

        {pageRentals.map((r) => (
          <RentalCard
            key={r.id}
            rental={r}
            customerLabel={customerName(r.customer_id)}
            canEdit={canEdit}
            onEdit={() => handleOpenEdit(r)}
            onDelete={() => handleDelete(r)}
            onChangeStatus={(next: Rental["status"]) => handleStatusChange(r, next)}
          />
        ))}
      </Box>

      {totalItems > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(limit) => {
            setItemsPerPage(limit);
            setCurrentPage(1);
          }}
          loading={loading}
        />
      )}

      <RentalCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleSubmit}
        products={products}
      />

      <RentalEditModal
        open={editing != null}
        rental={editing}
        canEdit={canEdit}
        onClose={() => setEditing(null)}
        onSave={update}
        onChanged={refreshEditing}
        onChangeStatus={async (next) => {
          if (!editing) return;
          await handleStatusChange(editing, next);
          await refreshEditing(editing.id);
        }}
        onReturnItem={returnItem}
        onRemoveItem={removeItem}
      />
    </Box>
  );
}
